import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { unreadByTask } from '@machs/shared';
import { useSync } from '../lib/sync';
import { showUnread } from '../lib/push';
import { Sidebar, type ListFilter } from './Sidebar';
import { CommandSearch } from './CommandSearch';
import { ChatView } from './ChatView';
import { BoardView } from './BoardView';
import { Dashboard } from './Dashboard';
import { DetailPanel } from './DetailPanel';
import { BusinessDialog, ProjectDialog, SubtaskDialog, TaskDialog, useBusinesses } from './dialogs';
import { SettingsPage } from './SettingsDialog';
import { ProfileDialog } from './ProfileDialog';
import { NotifyDialog } from './NotifyDialog';
import { ShareDialog } from './ShareDialog';
import { Logo } from './ui';

export type DialogState =
  | { kind: 'task'; projectId?: string }
  | { kind: 'subtask'; parentId: string }
  | { kind: 'project'; id?: string }
  | { kind: 'business'; id?: string }
  | { kind: 'profile' }
  | { kind: 'notify' }
  | { kind: 'share'; target: { taskId: string } | { projectId: string }; title: string }
  | null;

/** Wert aus der Adresse (?projekt=…&aufgabe=…). */
function query(name: string): string | null {
  return new URLSearchParams(location.search).get(name);
}

function setParam(url: URL, name: string, value: string | null) {
  if (value) url.searchParams.set(name, value);
  else url.searchParams.delete(name);
}

/** Stand der rechten Spalte für eine Aufgabe; ohne Eintrag bleibt sie offen. */
function detailOf(taskId: string | null): boolean {
  if (!taskId) return remembered('machs.detail', '1') === '1';
  return remembered(`machs.detail.${taskId}`, remembered('machs.detail', '1')) === '1';
}

function remembered(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

export function Shell({ onSwitchTeam }: { onSwitchTeam: () => void }) {
  const { client, me, version } = useSync();
  const businesses = useBusinesses();
  const [businessPick, setBusinessPick] = useState(() => remembered('machs.business', ''));
  // Nur ein Geschäft: keine Auswahl nötig, es gilt automatisch
  const businessId = businesses.find((b) => b.id === businessPick)?.id ?? businesses[0]?.id ?? null;
  // Projekt und Aufgabe stehen in der Adresse: ein Neuladen zeigt dieselbe Stelle
  // und Links lassen sich weitergeben (replaceState, damit der Verlauf nicht zuläuft).
  const [projectId, setProjectId] = useState<string | null>(() => query('projekt') ?? (remembered('machs.project', '') || null));
  const [filter, setFilter] = useState<ListFilter>('offen');
  const [taskId, setTaskId] = useState<string | null>(() => query('aufgabe'));
  useEffect(() => {
    const onPop = () => {
      setTaskId(query('aufgabe'));
      setProjectId(query('projekt'));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  // Auf schmalen Bildschirmen überdeckt der Detailbereich den Chat: dort nur auf Wunsch öffnen
  const narrow = () => window.matchMedia('(max-width: 1180px)').matches;
  // Je Aufgabe gemerkt: beim nächsten Öffnen steht die Spalte wieder so
  const [detail, setDetail] = useState(() => !narrow() && detailOf(query('aufgabe')));
  const [dialog, setDialog] = useState<DialogState>(null);
  // Übersicht: gewollt über das Logo, sonst zeigt ein Projekt seine eigene Ansicht
  const [home, setHome] = useState(false);
  // Schmale Seitenleiste: die Aufgaben bleiben als Ringe erreichbar
  const [rail, setRail] = useState(() => remembered('machs.rail', '0') === '1');
  // Einstellungen sind eine Seite im Hauptbereich, kein Dialog
  const [settings, setSettings] = useState(false);
  const [settingsStart, setSettingsStart] = useState<'team' | 'billing'>('team');
  const [settingsRequest, setSettingsRequest] = useState(0);
  const requestedDetails = useRef<string | null>(null);

  // Ungelesenes im Tab-Titel und am Symbol der installierten App
  const unreadTotal = useMemo(() => {
    let n = 0;
    for (const c of unreadByTask(client, me.user.id).values()) n += c;
    return n;
  }, [client, version, me.user.id]);
  const openTitle = taskId ? client.data.task.get(taskId)?.title ?? null : null;
  useEffect(() => showUnread(unreadTotal, openTitle), [unreadTotal, openTitle]);
  useEffect(() => () => showUnread(0), []);

  // Klick auf eine Benachrichtigung: die offene App springt zur Aufgabe
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      const msg = e.data as { type?: string; taskId?: string | null } | null;
      if (msg?.type !== 'machs:open' || !msg.taskId) return;
      const task = client.data.task.get(msg.taskId);
      const project = task ? client.data.project.get(task.projectId) : undefined;
      if (project?.businessId && project.businessId !== businessId) setBusinessPick(project.businessId);
      setTaskId(msg.taskId);
      setSettings(false);
      setHome(false);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [client, businessId]);

  // Adresse dem Zustand nachführen, ohne neuen Verlaufseintrag je Tastendruck
  useEffect(() => {
    const url = new URL(location.href);
    const before = url.search;
    setParam(url, 'projekt', projectId);
    setParam(url, 'aufgabe', taskId);
    if (url.search !== before) history.replaceState(null, '', `${url.pathname}${url.search}`);
  }, [projectId, taskId]);

  useEffect(() => {
    try {
      localStorage.setItem('machs.project', projectId ?? '');
      localStorage.setItem('machs.rail', rail ? '1' : '0');
      localStorage.setItem('machs.business', businessPick);
    } catch {
      // nur Komfort
    }
  }, [projectId, businessPick, rail]);

  /** Spalte auf- oder zuklappen und den Stand für diese Aufgabe merken. */
  const toggleDetail = () => {
    setDetail((offen) => {
      const next = !offen;
      try {
        if (taskId) localStorage.setItem(`machs.detail.${taskId}`, next ? '1' : '0');
        localStorage.setItem('machs.detail', next ? '1' : '0');
      } catch {
        // nur Komfort
      }
      return next;
    });
  };

  // Beim Öffnen einer anderen Aufgabe deren gemerkten Stand übernehmen
  useEffect(() => {
    const requested = requestedDetails.current === taskId && taskId !== null;
    requestedDetails.current = null;
    if (requested) setDetail(true);
    else if (taskId && !narrow()) setDetail(detailOf(taskId));
  }, [taskId]);

  // Gelöschtes oder fremdes Projekt nicht weiter vorauswählen
  useEffect(() => {
    const pr = projectId ? client.data.project.get(projectId) : null;
    if (projectId && (!pr || pr.archivedAt || pr.businessId !== businessId)) setProjectId(null);
  }, [client, projectId, businessId]);

  const task = taskId ? client.data.task.get(taskId) ?? null : null;
  // Die Ansicht gehört zum Projekt: „board" zeigt statt der Aufgabenliste ein Board
  const openProject = projectId ? client.data.project.get(projectId) ?? null : null;
  const boardOpen = !home && !settings && !task && openProject?.view === 'board';
  const accent = businesses.length > 1 ? (businesses.find((b) => b.id === businessId)?.color ?? null) : null;

  return (
    <div
      className={`shell ${task && detail ? 'with-detail' : ''} ${boardOpen ? 'board' : ''} ${rail ? 'rail' : ''}`}
      data-view={task || settings || boardOpen ? 'chat' : 'list'}
      // Jedes Geschäft hat seine Farbe: sie färbt Akzente in der ganzen App
      style={
        accent
          ? ({
              '--brand-a': accent,
              '--brand-b': `color-mix(in srgb, ${accent} 55%, white)`,
              '--brand': `linear-gradient(135deg, ${accent}, color-mix(in srgb, ${accent} 55%, white))`,
            } as CSSProperties)
          : undefined
      }
    >
      <aside className="pane-left">
        <Sidebar
          businessId={businessId}
          onBusiness={(id) => (setBusinessPick(id), setProjectId(null), setHome(false))}
          projectId={projectId}
          onProject={(id) => (setProjectId(id), setHome(false))}
          filter={filter}
          onFilter={setFilter}
          taskId={taskId}
          onOpenTask={(id) => {
            setTaskId(id);
            setSettings(false);
            setHome(false);
            if (narrow()) setDetail(false);
          }}
          onTaskDetails={(id) => {
            requestedDetails.current = id;
            try { localStorage.setItem(`machs.detail.${id}`, '1'); } catch { /* optional */ }
            setTaskId(id); setSettings(false); setHome(false); setDetail(true);
          }}
          onBilling={() => { setSettingsStart('billing'); setSettingsRequest(n => n + 1); setSettings(true); }}
          onDialog={setDialog}
          onHome={() => (setTaskId(null), setSettings(false), setHome(true))}
          rail={rail}
          onRail={setRail}
          boardView={openProject?.view === 'board'}
          onSettings={() => { setSettingsStart('team'); setSettingsRequest(n => n + 1); setSettings(true); }}
          onSwitchTeam={onSwitchTeam}
        />
      </aside>
      <main className="pane-mid">
        {settings ? (
          <SettingsPage initialTab={settingsStart} navigationRequest={settingsRequest} onClose={() => setSettings(false)} />
        ) : task ? (
          <ChatView
            key={task.id}
            task={task}
            onBack={() => setTaskId(null)}
            detailOpen={detail}
            onToggleDetail={toggleDetail}
            onEditProject={(id) => setDialog({ kind: 'project', id })}
            onShare={() => setDialog({ kind: 'share', target: { taskId: task.id }, title: task.title })}
            onOpenTask={setTaskId}
          />
        ) : boardOpen && projectId ? (
          <BoardView
            key={projectId}
            projectId={projectId}
            onOpenTask={(id) => {
              setTaskId(id);
              if (narrow()) setDetail(false);
            }}
            onNewTask={() => setDialog({ kind: 'task', projectId: projectId ?? undefined })}
            onNewSubtask={(parentId) => setDialog({ kind: 'subtask', parentId })}
          />
        ) : (
          <Dashboard
            businessId={businessId}
            onOpenTask={(id) => {
              setTaskId(id);
              setHome(false);
              if (narrow()) setDetail(false);
            }}
            onNewTask={() => setDialog({ kind: 'task', projectId: projectId ?? undefined })}
            onProject={(id) => (setProjectId(id), setHome(false))}
          />
        )}
      </main>
      {task && detail && (
        <aside className="pane-right">
          <DetailPanel
            task={task}
            onClose={toggleDetail}
            onShare={() => setDialog({ kind: 'share', target: { taskId: task.id }, title: task.title })}
            onEditProject={(id) => setDialog({ kind: 'project', id })}
            onOpenTask={setTaskId}
          />
        </aside>
      )}

      {dialog?.kind === 'task' && (
        <TaskDialog
          defaultBusinessId={businessId}
          defaultProjectId={dialog.projectId ?? null}
          onClose={() => setDialog(null)}
          onCreated={(id) => (setTaskId(id), setFilter('offen'))}
        />
      )}
      {dialog?.kind === 'subtask' && <SubtaskDialog parentId={dialog.parentId} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'project' && <ProjectDialog id={dialog.id} defaultBusinessId={businessId} onClose={() => setDialog(null)} onSaved={setProjectId} />}
      {dialog?.kind === 'business' && (
        <BusinessDialog
          id={dialog.id}
          onClose={() => setDialog(null)}
          onSaved={(id) => (setBusinessPick(id), setProjectId(null))}
          onDeleted={() => (setBusinessPick(''), setProjectId(null), setTaskId(null))}
        />
      )}
      {dialog?.kind === 'profile' && <ProfileDialog onClose={() => setDialog(null)} />}
      {dialog?.kind === 'notify' && <NotifyDialog onClose={() => setDialog(null)} />}
      {dialog?.kind === 'share' && <ShareDialog target={dialog.target} title={dialog.title} onClose={() => setDialog(null)} />}
      <CommandSearch
        onOpenTask={(id) => {
          setTaskId(id);
          setSettings(false);
          if (narrow()) setDetail(false);
        }}
        onNewTask={() => setDialog({ kind: 'task', projectId: projectId ?? undefined })}
      />
    </div>
  );
}

